import React, { useMemo } from "react";
import { CustomIcon } from "../shared/CustomIcon";

// =============================================================
// TreeHealthPanel — at-a-glance dashboard for admin diagnostics.
//   • Level bars (filled vs vacant per level)
//   • Total members vs total vacant slots
//   • Orphaned nodes needing reassignment
//   • Pending deletion requests with approval progress
// =============================================================
export default function TreeHealthPanel({ tree, orphaned, deletionRequests, onOpenAssignForOrphan }) {
  const nodes = useMemo(() => tree ? Object.values(tree.nodes || {}) : [], [tree]);

  const perLevel = useMemo(() => {
    const map = {};
    nodes.forEach(n => {
      const key = n.level;
      const b = map[key] || { level: key, name: n.levelName, total: 0, occupied: 0 };
      b.total += 1;
      if (n.memberId) b.occupied += 1;
      map[key] = b;
    });
    return Object.values(map).sort((a, b) => a.level - b.level);
  }, [nodes]);

  const totalMembers = nodes.filter(n => n.memberId).length;
  const totalVacant = nodes.filter(n => !n.memberId).length;
  const pendingRequests = (deletionRequests || []).filter(r => r.status === "pending");

  return (
    <div data-testid="tree-health-panel" style={{ background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Tree Health
        </div>
        <div style={{ fontSize: 11, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif" }}>
          {nodes.length} nodes · {totalMembers} occupied · {totalVacant} vacant
        </div>
      </div>

      {/* Level bars */}
      <div data-testid="health-levels" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {perLevel.map(b => {
          const pct = b.total ? Math.round((b.occupied / b.total) * 100) : 0;
          return (
            <div key={b.level} data-testid={`health-level-${b.level}`} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 90, fontFamily: "'Cinzel',serif", fontSize: 11, color: "var(--kc-accent)", letterSpacing: "0.06em" }}>
                {b.name}
              </div>
              <div style={{ flex: 1, height: 8, background: "rgba(255,255,255,0.06)", borderRadius: 999, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: "var(--kc-accent-grad)" }} />
              </div>
              <div style={{ width: 70, textAlign: "right", fontSize: 11, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif" }}>
                {b.occupied}/{b.total}
              </div>
            </div>
          );
        })}
        {perLevel.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--kc-sub)" }}>No levels yet. Initialize the tree first.</div>
        )}
      </div>

      {/* Orphaned */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 12, color: "#ff6b6b", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            Needs Reassignment
          </div>
          <span data-testid="health-orphan-count" style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, color: "#ff6b6b", border: "1px solid rgba(255,107,107,0.40)", background: "rgba(255,107,107,0.10)" }}>
            {(orphaned || []).length}
          </span>
        </div>
        {(orphaned || []).length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--kc-sub)" }}>No orphaned reports right now.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(orphaned || []).map(o => (
              <div key={o.nodeId} data-testid={`health-orphan-${o.nodeId}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 8, background: "rgba(255,107,107,0.06)", border: "1px solid rgba(255,107,107,0.20)", borderRadius: 8, gap: 8 }}>
                <div style={{ fontSize: 12, minWidth: 0, flex: 1 }}>
                  <strong>{o.slotRef}</strong> · #{o.memberNumber} · parent {o.parentSlotRef} ({o.parentStatus})
                </div>
                {onOpenAssignForOrphan && (
                  <button
                    className="btn btn-sm btn-outline"
                    data-testid={`health-orphan-reassign-${o.nodeId}`}
                    onClick={() => onOpenAssignForOrphan(o)}
                    style={{ whiteSpace: "nowrap", padding: "4px 10px" }}
                  >Assign to New Slot</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending deletion requests */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 12, color: "#f59e0b", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            Pending Deletion Requests
          </div>
          <span data-testid="health-del-count" style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, color: "#f59e0b", border: "1px solid rgba(245,158,11,0.40)", background: "rgba(245,158,11,0.10)" }}>
            {pendingRequests.length}
          </span>
        </div>
        {pendingRequests.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--kc-sub)" }}>No pending deletion requests.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {pendingRequests.map(r => (
              <div key={r.id} data-testid={`health-del-${r.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 8, background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.20)", borderRadius: 8, fontSize: 12 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div><strong>{r.targetType}</strong>: <code style={{ color: "var(--kc-accent)" }}>{r.targetId}</code></div>
                  <div style={{ fontSize: 10, color: "var(--kc-sub)", marginTop: 2 }}>by {r.requestedByName || r.requestedBy}</div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 999, color: "#f59e0b", border: "1px solid rgba(245,158,11,0.40)", whiteSpace: "nowrap" }}>
                  {(r.approvals || []).length}/3 <CustomIcon name="check" size={9} />
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
